import { apiRequest } from "./apiClient";

export type RechargeSummary = {
  id: number;
  accountId: number;
  accountNo: string;
  amount: number;
  pumpId?: number | null;
  msisdn?: string | null;
  transactionStatus?: string | null;
  transactionReference?: string | null;
  transactionDate: string;
  rechargeType?: string | null;
  balance?: number | null;
};

export type RechargeDetail = RechargeSummary & {
  currency?: string | null;
  creditOrganizationName?: string | null;
  creditShortCode?: string | null;
};

export type RechargeSavePayload = {
  transactionTypeId: number;
  amount: number;
  accountNo?: string;
  cardNumber?: string;
  referenceNo?: string | null;
  pumpId?: number;
};

export type PaginationMeta = {
  page: number;
  pageSize: number;
  total: number;
  pages: number;
};

export type PaginatedResponse<T> = {
  items: T[];
  meta: PaginationMeta;
};

export type RechargeTypeOption = {
  id: number;
  name?: string | null;
  transactionTypeName?: string | null;
  code?: string | null;
  paymentProvider?: string | null;
  isEnabled?: boolean;
};

export type FetchRechargesOptions = {
  page?: number;
  pageSize?: number;
  search?: string;
  fromDate?: string;
  toDate?: string;
  pumpId?: number;
  signal?: AbortSignal;
};

const buildQuery = (options?: FetchRechargesOptions) => {
  if (!options) {
    return "";
  }

  const params = new URLSearchParams();
  if (options.page && options.page > 0) {
    params.append("page", String(options.page));
  }
  if (options.pageSize && options.pageSize > 0) {
    params.append("pageSize", String(options.pageSize));
  }
  if (options.search?.trim()) {
    params.append("search", options.search.trim());
  }
  if (options.fromDate) {
    params.append("fromDate", options.fromDate);
  }
  if (options.toDate) {
    params.append("toDate", options.toDate);
  }
  if (options.pumpId) {
    params.append("pumpId", String(options.pumpId));
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};

export function fetchRecharges(options?: FetchRechargesOptions) {
  const query = buildQuery(options);
  return apiRequest<PaginatedResponse<RechargeSummary>>(`/api/recharges${query}`, {
    signal: options?.signal,
  });
}

export function fetchRechargeDetail(rechargeId: number | string, signal?: AbortSignal) {
  return apiRequest<RechargeDetail>(`/api/recharges/${rechargeId}`, { signal });
}

export function createRecharge(payload: RechargeSavePayload) {
  return apiRequest<RechargeDetail>(`/api/recharges`, {
    method: "POST",
    body: payload,
  });
}

export function updateRecharge(rechargeId: number | string, payload: RechargeSavePayload) {
  return apiRequest<RechargeDetail>(`/api/recharges/${rechargeId}`, {
    method: "PUT",
    body: payload,
  });
}

export function deleteRecharge(rechargeId: number | string) {
  return apiRequest<void>(`/api/recharges/${rechargeId}`, {
    method: "DELETE",
  });
}

export function fetchRechargeTypes(signal?: AbortSignal) {
  return apiRequest<RechargeTypeOption[]>(`/api/recharges/types`, { signal });
}
